import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { ApiClientError, classifyApiError, type ApiErrorKind } from '@shared/api/api-client';
import { logger } from './logger';
import { performanceMonitor } from './performance-monitor';
import { toast } from 'sonner';

export type QueryDomain =
  | 'quotingQueue'
  | 'travelPlanning'
  | 'userManagement'
  | 'reference'
  | 'default';

export type QueryDomainPolicy = {
  staleTime: number;
  gcTime: number;
  retry: (failureCount: number, error: unknown) => boolean;
};

const buildDomainRetry = (allowedKinds: ApiErrorKind[], maxRetries: number) => {
  return (failureCount: number, error: unknown) => {
    if (failureCount >= maxRetries) return false;
    return allowedKinds.includes(classifyApiError(error));
  };
};

/**
 * When React Query cancels an in-flight fetch (invalidate, new queryKey, prefetch),
 * the rejection is often an AbortError. Do not treat that as a user-facing failure
 * if we still have cached rows or another fetch is in progress.
 */
export const shouldShowQueryFetchFailure = (options: {
  isError: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  hasCachedData: boolean;
}): boolean => {
  const { isError, isLoading, isFetching, error, hasCachedData } = options;
  if (!isError || isLoading) return false;
  if (classifyApiError(error) !== 'abort') return true;
  if (hasCachedData || isFetching) return false;
  return true;
};

export const queryDomainPolicy = (domain: QueryDomain): QueryDomainPolicy => {
  switch (domain) {
    case 'quotingQueue':
      return {
        staleTime: 60 * 1000,
        gcTime: 15 * 60 * 1000,
        retry: buildDomainRetry(['timeout', 'network', 'server'], 3),
      };
    case 'travelPlanning':
      return {
        staleTime: 60 * 1000,
        gcTime: 20 * 60 * 1000,
        retry: buildDomainRetry(['timeout', 'network', 'server'], 2),
      };
    case 'userManagement':
      return {
        staleTime: 45 * 1000,
        gcTime: 15 * 60 * 1000,
        retry: buildDomainRetry(['timeout', 'network', 'server'], 2),
      };
    case 'reference':
      return {
        staleTime: 30 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
        retry: buildDomainRetry(['network', 'timeout'], 1),
      };
    default:
      return {
        staleTime: 5 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        retry: buildDomainRetry(['timeout', 'network', 'server'], 3),
      };
  }
};

// Enhanced query cache with performance monitoring
const queryCache = new QueryCache({
  onError: (error, query) => {
    if (classifyApiError(error) === 'abort') {
      return;
    }
    logger.error('Query failed', {
      queryKey: query.queryKey,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  },
});

// Enhanced mutation cache with performance monitoring
const mutationCache = new MutationCache({
  onError: (error, variables, _context, mutation) => {
    if (classifyApiError(error) === 'abort') {
      return;
    }
    logger.error('Mutation failed', {
      mutationKey: mutation.options.mutationKey,
      error: error instanceof Error ? error.message : 'Unknown error',
      variables,
    });
  },
});

// Default query options with enhanced configuration
const defaultQueryOptions = {
  queries: {
    // Stale time: how long data is considered fresh
    staleTime: (context: any) => {
      // Different stale times based on data type
      const queryKey = context.queryKey?.[0];
      if (queryKey === 'quoting-queue') return queryDomainPolicy('quotingQueue').staleTime;
      if (queryKey === 'travel-planning') return queryDomainPolicy('travelPlanning').staleTime;
      if (queryKey === 'user-management') return queryDomainPolicy('userManagement').staleTime;
      if (queryKey === 'procedures' || queryKey === 'specialties') {
        return queryDomainPolicy('reference').staleTime;
      }
      if (queryKey === 'payments') return 0;
      if (queryKey === 'super-admin') return 2 * 60 * 1000;
      if (queryKey === 'user') return 10 * 60 * 1000;
      if (queryKey === 'dashboard') return 1 * 60 * 1000;
      return queryDomainPolicy('default').staleTime;
    },

    // Cache time: how long data stays in cache when not being used
    gcTime: queryDomainPolicy('default').gcTime,

    // Retry configuration with intelligent backoff
    retry: (failureCount: number, error: unknown) => {
      if (error instanceof ApiClientError && error.status === 401) return false;
      if (error instanceof ApiClientError && error.status === 429) return false;
      if (error instanceof ApiClientError && error.status >= 400 && error.status < 500) {
        return error.status === 408 ? failureCount < 2 : false;
      }
      return queryDomainPolicy('default').retry(failureCount, error);
    },

    // Retry delay with exponential backoff and jitter
    retryDelay: (attemptIndex: number, error: unknown) => {
      const baseDelay = 1000 * Math.pow(2, attemptIndex);
      const jitter = Math.random() * 1000; // Add jitter to prevent thundering herd
      const maxDelay = 30000; // Max 30 seconds

      // Longer delays for rate limit errors
      if (error instanceof ApiClientError && error.status === 429) {
        return Math.min(baseDelay * 2 + jitter, 60000); // Max 1 minute for rate limits
      }

      return Math.min(baseDelay + jitter, maxDelay);
    },

    // Refetch on window focus for critical data only
    refetchOnWindowFocus: (query: any) => {
      const queryKey = query.queryKey?.[0];
      return queryKey === 'super-admin' || queryKey === 'dashboard';
    },

    // Refetch on reconnect
    refetchOnReconnect: true,

    // Network mode for better offline handling
    networkMode: 'online' as const,

    // Refetch interval for real-time data
    refetchInterval: (query: any) => {
      const queryKey = query.queryKey?.[0];

      // Auto-refresh critical data
      if (queryKey === 'super-admin' && query.queryKey?.[1] === 'metrics') {
        return 30 * 1000; // 30 seconds for system metrics
      }

      if (queryKey === 'super-admin' && query.queryKey?.[1] === 'alerts') {
        return 60 * 1000; // 1 minute for alerts
      }

      return false; // No auto-refresh by default
    },

    // Refetch interval in background
    refetchIntervalInBackground: false,
  },

  mutations: {
    // Retry mutations with intelligent logic
    retry: (failureCount: number, error: unknown) => {
      // Retry network errors once
      if (error instanceof ApiClientError && error.code === 'NETWORK_ERROR') {
        return failureCount < 1;
      }

      // Retry timeout errors twice
      if (error instanceof ApiClientError && error.code === 'REQUEST_TIMEOUT') {
        return failureCount < 2;
      }
      if (error instanceof ApiClientError && error.status === 429) return false;

      return false;
    },

    // Mutation retry delay
    retryDelay: (attemptIndex: number, error: unknown) => {
      const baseDelay = 2000 * Math.pow(2, attemptIndex);

      // Longer delay for rate limits
      if (error instanceof ApiClientError && error.status === 429) {
        return Math.min(baseDelay * 3, 60000);
      }

      return Math.min(baseDelay, 30000);
    },

    // Network mode for mutations
    networkMode: 'online' as const,
  },
};

// Create the query client with enhanced configuration
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: defaultQueryOptions,
});

// Query key factories for consistent cache keys
export const queryKeys = {
  // User queries
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    enhancedProfile: () => [...queryKeys.user.all, 'enhanced-profile'] as const,
  },

  // Procedures queries
  procedures: {
    all: ['procedures'] as const,
    lists: () => [...queryKeys.procedures.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.procedures.lists(), params] as const,
    details: () => [...queryKeys.procedures.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.procedures.details(), id] as const,
  },

  specialties: {
    all: ['specialties'] as const,
    intake: () => [...queryKeys.specialties.all, 'intake'] as const,
  },

  // Appointments queries
  appointments: {
    all: ['appointments'] as const,
    lists: () => [...queryKeys.appointments.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.appointments.lists(), params] as const,
    details: () => [...queryKeys.appointments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.appointments.details(), id] as const,
    myTrips: (params?: Record<string, any>) =>
      [...queryKeys.appointments.all, 'my-trips', params] as const,
    byTrip: (tripId: string, params?: Record<string, any>) =>
      [...queryKeys.appointments.all, 'trip', tripId, params] as const,
  },

  doctorQuotes: {
    all: ['doctor-quotes'] as const,
    lists: () => [...queryKeys.doctorQuotes.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.doctorQuotes.lists(), params] as const,
    workspace: (requestId?: string | null) =>
      [...queryKeys.doctorQuotes.all, 'workspace', requestId] as const,
    submit: (requestId: string) => [...queryKeys.doctorQuotes.all, 'submit', requestId] as const,
    intake: (
      doctorId?: string | null,
      quoteId?: string | null,
      requestId?: string | null,
      refId?: string | null
    ) => [...queryKeys.doctorQuotes.all, 'intake', doctorId, quoteId, requestId, refId] as const,
  },

  doctorActivation: {
    all: ['doctor-activation'] as const,
    state: (doctorId?: string | null, language?: string | null) =>
      [...queryKeys.doctorActivation.all, 'state', doctorId, language] as const,
    summary: (doctorId?: string | null, language?: string | null) =>
      [...queryKeys.doctorActivation.all, 'summary', doctorId, language] as const,
    publicProfile: (slug: string) =>
      [...queryKeys.doctorActivation.all, 'public-profile', slug] as const,
  },

  patientQuotes: {
    all: ['patient-quotes'] as const,
    dashboard: () => [...queryKeys.patientQuotes.all, 'dashboard'] as const,
    travelPlan: (travelCaseId: string) =>
      [...queryKeys.patientQuotes.all, 'travel-plan', travelCaseId] as const,
  },

  payments: {
    all: ['payments'] as const,
    history: (params?: Record<string, any>) =>
      [...queryKeys.payments.all, 'history', params] as const,
    consultationFees: () => [...queryKeys.payments.all, 'consultation-fees'] as const,
    adminConsultationFees: (params?: Record<string, any>) =>
      [...queryKeys.payments.all, 'admin-consultation-fees', params] as const,
    consultationDoctors: (q?: string) =>
      [...queryKeys.payments.all, 'consultation-doctors', q] as const,
    projection: (travelCaseId?: string | null) =>
      [...queryKeys.payments.all, 'projection', travelCaseId] as const,
    workspace: (actor: 'admin' | 'superadmin') =>
      [...queryKeys.payments.all, 'workspace', actor] as const,
    workspacePatients: (actor: 'admin' | 'superadmin', params?: Record<string, any>) =>
      [...queryKeys.payments.workspace(actor), 'patients', params] as const,
    workspacePatient: (
      actor: 'admin' | 'superadmin',
      patientId?: string | null,
      params?: Record<string, any>
    ) => [...queryKeys.payments.workspace(actor), 'patient', patientId, params] as const,
    workspaceDoctors: (actor: 'admin' | 'superadmin', params?: Record<string, any>) =>
      [...queryKeys.payments.workspace(actor), 'doctors', params] as const,
    workspaceDoctor: (
      actor: 'admin' | 'superadmin',
      doctorId?: string | null,
      params?: Record<string, any>
    ) => [...queryKeys.payments.workspace(actor), 'doctor', doctorId, params] as const,
    workspaceOrganizations: (actor: 'admin' | 'superadmin', params?: Record<string, any>) =>
      [...queryKeys.payments.workspace(actor), 'organizations', params] as const,
    workspaceOrganization: (
      actor: 'admin' | 'superadmin',
      organizationId?: string | null,
      params?: Record<string, any>
    ) => [...queryKeys.payments.workspace(actor), 'organization', organizationId, params] as const,
    workspaceOrganizationDoctor: (
      actor: 'admin' | 'superadmin',
      organizationId?: string | null,
      doctorId?: string | null,
      params?: Record<string, any>
    ) =>
      [
        ...queryKeys.payments.workspace(actor),
        'organization-doctor',
        organizationId,
        doctorId,
        params,
      ] as const,
    workspaceHistory: (actor: 'admin' | 'superadmin', params?: Record<string, any>) =>
      [...queryKeys.payments.workspace(actor), 'history', params] as const,
  },

  // Trips queries
  trips: {
    all: ['trips'] as const,
    lists: () => [...queryKeys.trips.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.trips.lists(), params] as const,
    details: () => [...queryKeys.trips.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.trips.details(), id] as const,
    byBooking: (bookingId: string) => [...queryKeys.trips.all, 'booking', bookingId] as const,
  },

  // Quotes queries (stubs)
  quotes: {
    all: ['quotes'] as const,
    lists: () => [...queryKeys.quotes.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.quotes.lists(), params] as const,
    serviceRequests: (params?: Record<string, any>) =>
      [...queryKeys.quotes.all, 'service-requests', params] as const,
  },

  // Patients queries (stubs)
  patients: {
    all: ['patients'] as const,
    lists: () => [...queryKeys.patients.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.patients.lists(), params] as const,
  },

  // Dashboard queries (stubs)
  dashboard: {
    all: ['dashboard'] as const,
    metrics: () => [...queryKeys.dashboard.all, 'metrics'] as const,
  },

  // Calendar queries (stubs)
  calendar: {
    all: ['calendar'] as const,
    connections: () => [...queryKeys.calendar.all, 'connections'] as const,
    providerCalendars: (connectionId: string) =>
      [...queryKeys.calendar.all, 'provider-calendars', connectionId] as const,
    events: (params: { start: string; end: string; doctorId?: string | null }) =>
      [...queryKeys.calendar.all, 'events', params] as const,
    externalEvents: (params: { start: string; end: string }) =>
      [...queryKeys.calendar.all, 'external-events', params] as const,
  },

  // Notifications queries (stubs)
  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.notifications.lists(), params] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
    governance: (userId: string) => [...queryKeys.notifications.all, 'governance', userId] as const,
  },

  patientHome: {
    all: ['patient-home'] as const,
    stats: (patientId: string) => [...queryKeys.patientHome.all, 'stats', patientId] as const,
    activity: (patientId: string) => [...queryKeys.patientHome.all, 'activity', patientId] as const,
    intakeForms: (patientId: string) =>
      [...queryKeys.patientHome.all, 'intake-forms', patientId] as const,
    intakeFormDetail: (patientId: string, formUid?: string | null) =>
      [...queryKeys.patientHome.all, 'intake-form-detail', patientId, formUid] as const,
    pendingFlightInformation: () =>
      [...queryKeys.patientHome.all, 'pending-flight-information'] as const,
    // PR7: trip 2+ appends the trip number; trip 1 keeps today's key.
    flightDetails: (serviceRequestId?: string | null, tripNumber?: number) =>
      tripNumber && tripNumber > 1
        ? ([...queryKeys.patientHome.all, 'flight-details', serviceRequestId, tripNumber] as const)
        : ([...queryKeys.patientHome.all, 'flight-details', serviceRequestId] as const),
  },

  patientTravel: {
    all: ['patient-travel'] as const,
    pendingFlightInformation: () =>
      [...queryKeys.patientTravel.all, 'pending-flight-information'] as const,
    flightInformation: (
      travelCaseId?: string | null,
      serviceRequestId?: string | null,
      tripNumber?: number
    ) =>
      tripNumber && tripNumber > 1
        ? ([
            ...queryKeys.patientTravel.all,
            'flight-information',
            travelCaseId,
            serviceRequestId,
            tripNumber,
          ] as const)
        : ([
            ...queryKeys.patientTravel.all,
            'flight-information',
            travelCaseId,
            serviceRequestId,
          ] as const),
  },

  workflow: {
    all: ['workflow'] as const,
    serviceRequest: (serviceRequestId?: string | null) =>
      [...queryKeys.workflow.all, 'service-request', serviceRequestId] as const,
    serviceRequestDocRequests: (serviceRequestId?: string | null) =>
      [...queryKeys.workflow.serviceRequest(serviceRequestId), 'doc-requests'] as const,
    board: (queryKey: string) => [...queryKeys.workflow.all, queryKey] as const,
  },

  careJourney: {
    all: ['care-journey'] as const,
    trips: (travelCaseId?: string | null) =>
      [...queryKeys.careJourney.all, 'trips', travelCaseId] as const,
    aftercare: (serviceRequestId?: string | null) =>
      [...queryKeys.careJourney.all, 'aftercare', serviceRequestId] as const,
    tripSummaries: (serviceRequestIds: string[]) =>
      [...queryKeys.careJourney.all, 'trip-summaries', serviceRequestIds] as const,
    queue: (tab: string) => [...queryKeys.careJourney.all, 'queue', tab] as const,
    myIssues: () => [...queryKeys.careJourney.all, 'my-issues'] as const,
  },

  flightInformation: {
    all: ['flight-information'] as const,
    serviceRequest: (serviceRequestId?: string | null, tripNumber?: number) =>
      tripNumber && tripNumber > 1
        ? ([...queryKeys.flightInformation.all, 'service-request', serviceRequestId, tripNumber] as const)
        : ([...queryKeys.flightInformation.all, 'service-request', serviceRequestId] as const),
    travelCase: (travelCaseId?: string | null, tripNumber?: number) =>
      tripNumber && tripNumber > 1
        ? ([...queryKeys.flightInformation.all, 'travel-case', travelCaseId, tripNumber] as const)
        : ([...queryKeys.flightInformation.all, 'travel-case', travelCaseId] as const),
  },

  quotingQueue: {
    all: ['quoting-queue'] as const,
    list: (params?: Record<string, unknown>) => [...queryKeys.quotingQueue.all, params] as const,
    patient: (patientKey?: string | null) =>
      [...queryKeys.quotingQueue.all, 'patient', patientKey] as const,
    serviceRequest: (serviceRequestId?: string | null) =>
      [...queryKeys.quotingQueue.all, 'service-request', serviceRequestId] as const,
    docs: (serviceRequestId?: string | null) =>
      [...queryKeys.quotingQueue.all, 'docs', serviceRequestId] as const,
  },

  travelPlanning: {
    all: ['travel-planning'] as const,
    detail: (travelCaseId?: string | null) =>
      [...queryKeys.travelPlanning.all, travelCaseId] as const,
    catalog: (searchQuery?: string) =>
      [...queryKeys.travelPlanning.all, 'catalog', searchQuery] as const,
  },

  doctor: {
    all: ['doctor'] as const,
    sidebarProfile: (doctorId?: string | null) =>
      [...queryKeys.doctor.all, 'sidebar-profile', doctorId] as const,
    profile: (doctorId?: string | null) => [...queryKeys.doctor.all, 'profile', doctorId] as const,
    patients: (searchQuery?: string, filters?: unknown) =>
      [...queryKeys.doctor.all, 'patients', searchQuery, filters] as const,
    patientsUnreadCount: () => [...queryKeys.doctor.all, 'patients', 'unread-count'] as const,
    patientDetails: (patientId?: string | null) =>
      [...queryKeys.doctor.all, 'patient-details', patientId] as const,
    myDayRequest: (requestId?: string | null) =>
      [...queryKeys.doctor.all, 'my-day', 'new-requests', requestId] as const,
  },

  docsPortal: {
    all: ['docs-portal'] as const,
    requests: (token?: string | null) => [...queryKeys.docsPortal.all, token, 'requests'] as const,
  },

  flightsPortal: {
    all: ['flights-portal'] as const,
    detail: (token?: string | null) => [...queryKeys.flightsPortal.all, token] as const,
  },

  ownershipTransfer: {
    all: ['ownership-transfer'] as const,
    detail: (token?: string | null) => [...queryKeys.ownershipTransfer.all, token] as const,
  },

  doctorInvitation: {
    all: ['doctor-invitation'] as const,
    detail: (token?: string | null) => [...queryKeys.doctorInvitation.all, token] as const,
  },

  eligibleRequests: {
    all: ['doctors', 'eligible-requests'] as const,
    list: (params: { scope?: string; page: number; limit: number; search?: string }) =>
      [
        ...queryKeys.eligibleRequests.all,
        params.scope ?? '',
        params.page,
        params.limit,
        params.search ?? '',
      ] as const,
  },

  // Files queries
  files: {
    all: ['files'] as const,
    presignedUrl: (category: string) =>
      [...queryKeys.files.all, 'presigned-url', category] as const,
    presignedDownload: (fileId: string) =>
      [...queryKeys.files.all, 'presigned-download', fileId] as const,
    imageUrl: (storagePath?: string | null) => ['file-url', storagePath] as const,
  },

  // Super Admin queries
  superAdmin: {
    all: ['super-admin'] as const,
    metrics: () => [...queryKeys.superAdmin.all, 'metrics'] as const,
    recentCases: (limit?: number) => [...queryKeys.superAdmin.all, 'recent-cases', limit] as const,
    revenue: () => [...queryKeys.superAdmin.all, 'revenue'] as const,
    alerts: (limit?: number) => [...queryKeys.superAdmin.all, 'alerts', limit] as const,
  },
} as const;

// Utility function to invalidate related queries
export const invalidateQueries = {
  user: () => queryClient.invalidateQueries({ queryKey: queryKeys.user.all }),
  procedures: () => queryClient.invalidateQueries({ queryKey: queryKeys.procedures.all }),
  appointments: () => queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all }),
  trips: () => queryClient.invalidateQueries({ queryKey: queryKeys.trips.all }),
  quotes: () => queryClient.invalidateQueries({ queryKey: queryKeys.quotes.all }),
  patients: () => queryClient.invalidateQueries({ queryKey: queryKeys.patients.all }),
  dashboard: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  calendar: () => queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all }),
  notifications: () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all }),
  patientHome: () => queryClient.invalidateQueries({ queryKey: queryKeys.patientHome.all }),
  patientTravel: () => queryClient.invalidateQueries({ queryKey: queryKeys.patientTravel.all }),
  workflow: () => queryClient.invalidateQueries({ queryKey: queryKeys.workflow.all }),
  careJourney: () => queryClient.invalidateQueries({ queryKey: queryKeys.careJourney.all }),
  flightInformation: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.flightInformation.all }),
  quotingQueue: () => queryClient.invalidateQueries({ queryKey: queryKeys.quotingQueue.all }),
  travelPlanning: () => queryClient.invalidateQueries({ queryKey: queryKeys.travelPlanning.all }),
  doctor: () => queryClient.invalidateQueries({ queryKey: queryKeys.doctor.all }),
  docsPortal: () => queryClient.invalidateQueries({ queryKey: queryKeys.docsPortal.all }),
  flightsPortal: () => queryClient.invalidateQueries({ queryKey: queryKeys.flightsPortal.all }),
  ownershipTransfer: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.ownershipTransfer.all }),
  doctorInvitation: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.doctorInvitation.all }),
  eligibleRequests: () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.eligibleRequests.all }),
  files: () => queryClient.invalidateQueries({ queryKey: queryKeys.files.all }),
  superAdmin: () => queryClient.invalidateQueries({ queryKey: queryKeys.superAdmin.all }),
};

// Enhanced cache management utilities
export const cacheUtils = {
  /**
   * Prefetch data for better performance
   */
  prefetchQuery: async <T>(
    queryKey: readonly unknown[],
    queryFn: () => Promise<T>,
    options?: {
      staleTime?: number;
      gcTime?: number;
    }
  ) => {
    const startTime = performance.now();

    try {
      await queryClient.prefetchQuery({
        queryKey,
        queryFn,
        staleTime: options?.staleTime || 5 * 60 * 1000,
        gcTime: options?.gcTime || 10 * 60 * 1000,
      });

      const duration = performance.now() - startTime;
      performanceMonitor.recordMetric('query_prefetch_duration', duration, 'ms', {
        queryKey: JSON.stringify(queryKey),
      });
    } catch (error) {
      logger.error('Query prefetch failed', {
        queryKey,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  },

  /**
   * Optimistically update cache
   */
  optimisticUpdate: <T>(
    queryKey: readonly unknown[],
    updater: (oldData: T | undefined) => T,
    rollbackFn?: () => void
  ) => {
    const previousData = queryClient.getQueryData<T>(queryKey);

    // Optimistically update
    queryClient.setQueryData(queryKey, updater);

    // Return rollback function
    return () => {
      queryClient.setQueryData(queryKey, previousData);
      if (rollbackFn) rollbackFn();
    };
  },

  /**
   * Batch invalidate multiple queries
   */
  batchInvalidate: (queryKeys: readonly unknown[][]) => {
    queryKeys.forEach(queryKey => {
      queryClient.invalidateQueries({ queryKey });
    });
  },

  /**
   * Smart cache warming for related data
   */
  warmRelatedCache: async (entityType: string, entityId: string) => {
    const warmingPromises: Promise<any>[] = [];

    switch (entityType) {
      case 'organization':
        // Warm organization-related caches
        warmingPromises.push(
          cacheUtils.prefetchQuery(
            ['super-admin', 'organizations', 'detail', entityId],
            () => Promise.resolve(null) // Would be actual API call
          )
        );
        break;

      case 'user':
        // Warm user-related caches
        warmingPromises.push(
          cacheUtils.prefetchQuery(
            ['super-admin', 'users', 'detail', entityId],
            () => Promise.resolve(null) // Would be actual API call
          )
        );
        break;
    }

    await Promise.allSettled(warmingPromises);
  },

  /**
   * Clean up stale cache entries
   */
  cleanupStaleCache: () => {
    const cacheTime = Date.now() - 30 * 60 * 1000; // 30 minutes ago

    queryClient
      .getQueryCache()
      .getAll()
      .forEach(query => {
        if (query.state.dataUpdatedAt < cacheTime && !query.getObserversCount()) {
          queryClient.removeQueries({ queryKey: query.queryKey });
        }
      });
  },

  /**
   * Get cache statistics
   */
  getCacheStats: () => {
    const queries = queryClient.getQueryCache().getAll();
    const mutations = queryClient.getMutationCache().getAll();

    const stats = {
      totalQueries: queries.length,
      activeQueries: queries.filter(q => q.getObserversCount() > 0).length,
      staleQueries: queries.filter(q => q.isStale()).length,
      errorQueries: queries.filter(q => q.state.status === 'error').length,
      totalMutations: mutations.length,
      pendingMutations: mutations.filter(m => m.state.status === 'pending').length,
      cacheSize: JSON.stringify(queries.map(q => q.state.data)).length,
    };

    return stats;
  },
};

// Enhanced error handler for React Query
export const handleQueryError = (error: unknown, context?: { queryKey?: readonly unknown[] }) => {
  const startTime = performance.now();

  if (error instanceof ApiClientError) {
    const errorInfo = {
      message: error.message,
      status: error.status,
      code: error.code,
      details: error.details,
      queryKey: context?.queryKey,
    };

    logger.error('Query error occurred', errorInfo);

    // Record performance metric for error handling
    const duration = performance.now() - startTime;
    performanceMonitor.recordMetric('query_error_handling_duration', duration, 'ms', {
      errorCode: error.code || 'unknown',
      status: error.status?.toString() || 'unknown',
    });

    // Handle specific error types
    switch (error.code) {
      case 'AUTHENTICATION_REQUIRED':
        logger.securityEvent('authentication_required', 'medium', {
          queryKey: context?.queryKey,
          status: error.status,
        });
        // In a real app, redirect to login
        break;

      case 'FORBIDDEN':
        // Check if this is a suspension error
        if (error.message.includes('suspended') || error.status === 403) {
          logger.securityEvent('user_suspended', 'high', {
            queryKey: context?.queryKey,
            status: error.status,
          });
          // Only redirect if not already on auth error page to avoid conflicts with AuthProvider
          if (!window.location.pathname.includes('/auth/error')) {
            window.location.href = `/auth/error?message=${encodeURIComponent(error.message)}&code=SUSPENDED`;
          }
        }
        break;

      case 'RATE_LIMIT_EXCEEDED':
        logger.performanceMetric('rate_limit_hit', 1, 'count');
        // Show user-friendly toast message
        toast.error('Too many requests', {
          description:
            'Please wait a moment before trying again. The request will be retried automatically.',
          duration: 5000,
        });
        break;

      case 'NETWORK_ERROR':
        logger.performanceMetric('network_error', 1, 'count');
        // Could show offline indicator
        break;

      case 'REQUEST_TIMEOUT':
        logger.performanceMetric('request_timeout', 1, 'count');
        // Could suggest retry
        break;

      default:
        logger.error('Unhandled API error', errorInfo);
    }
  } else {
    logger.error('Unknown query error', {
      error: error instanceof Error ? error.message : 'Unknown error',
      queryKey: context?.queryKey,
    });
  }
};

// Query performance monitoring
export const withQueryPerformanceMonitoring = <T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>
) => {
  return async (): Promise<T> => {
    const startTime = performance.now();
    const queryKeyString = JSON.stringify(queryKey);

    performanceMonitor.startMeasure(`query_${queryKeyString}`);

    try {
      const result = await queryFn();
      const duration = performance.now() - startTime;

      performanceMonitor.recordMetric('query_duration', duration, 'ms', {
        queryKey: queryKeyString,
        success: 'true',
      });

      performanceMonitor.endMeasure(`query_${queryKeyString}`);

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;

      performanceMonitor.recordMetric('query_duration', duration, 'ms', {
        queryKey: queryKeyString,
        success: 'false',
        error: error instanceof Error ? error.message : 'unknown',
      });

      performanceMonitor.endMeasure(`query_${queryKeyString}`);

      throw error;
    }
  };
};
